/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx, css } from "@emotion/core"
import { FC, Fragment, useCallback, useState, useEffect } from "react"
import { Drawer } from "antd"
import { Form, Row, Col, Button } from "grab-ui"
import { useTranslationsMap } from "common/utils/i18n"
import { useDispatch, useSelector } from "react-redux"
import { AppState } from "store/store.type"
import { actionCreator } from "store/store"
import { ResponseWithError } from "types/api"
import { OutletStore } from "types/models/menu"
import { ChevronLeftUniversal } from "@duxton-web/icons"
import notification from "components/common/notification"
import {
  EventAction,
  EventCategory,
  EventCategoryEng,
  EventActionEng,
} from "constants/tracking"
import { MenuStoreList } from "pages/menu"
import {
  getResponseErrorEventLabels,
  getResponseErrorEventLabelsForRTK,
  TroyWebTracker,
} from "utils/tracking"
import useFetchAllMenuGroupLinkedStoreList from "hooks/useFetchAllMenuGroupLinkedStoreList"
import { useHistory } from "react-router"
import { getGrabXValueFromLocalStorage, MerchantPortalFeatureFlag } from "grabx"
import useRtkQueryMigrationEndpointsFlag from "hooks/useRtkQueryMigrationEndpointsFlag"
import { GetErrorNotificationContent } from "queries/apiClient"
import {
  useLazyFetchMenuGroupDetailQuery,
  useLinkStoreToMenuGroupMutation,
} from "queries/menuApi"
import {
  menuGroupLinkStoreDrawerButton,
  menuGroupLinkStoreDrawerFooter,
  menuGroupLinkStoreDrawerStyle,
} from "./styles"
import { refreshMenuGroupAfterStoreChange } from "../menuGroupStoreRefresh"
import { drawerFooter, drawerTitleStyle } from "../styles"

const sendLinkStoreConfirmEvents = ({
  t0,
  labels,
  merchantGroupID,
  isRtkEnabled,
  fromRtkQuery,
}: {
  t0: number
  labels: Record<string, unknown>
  merchantGroupID: string
  isRtkEnabled: boolean
  fromRtkQuery: boolean
}) => {
  const t1 = performance.now()
  TroyWebTracker.sendEvent({
    category: EventCategory.menuGroupAddStores,
    action: EventAction.clickConfirm,
    labels: {
      ...labels,
      LOAD_TIME: t1 - t0,
    },
  })
  TroyWebTracker.sendEvent({
    category: EventCategoryEng.rtkMigration,
    action: EventActionEng.linkStoreToMenuGroup,
    labels: {
      MERCHANT_ID: merchantGroupID,
      IS_RTK_ENABLED: isRtkEnabled,
      FROM_RTK_QUERY: fromRtkQuery,
      FROM_OLD_REDUX: !fromRtkQuery,
      ACTION_NAME: "linkStoreToMenuGroup",
    },
  })
}

export interface MenuGroupLinkStoreDrawerProps extends FormComponentProps {
  visible: boolean
  onClose: () => void
}

export const MenuGroupLinkStoreDrawerComponent: FC<MenuGroupLinkStoreDrawerProps> = ({
  visible,
  onClose,
}: MenuGroupLinkStoreDrawerProps) => {
  const {
    merchantGroupID,
    menuGroupDetail: { menuGroupID },
    menuGroupLinkedStoresIDs = [],
  } = useSelector((state: AppState) => state.menu)

  const dispatch = useDispatch()
  const history = useHistory()
  const fetchAllMenuGroupLinkedStoreList = useFetchAllMenuGroupLinkedStoreList()

  const {
    flags: {
      linkStoreToMenuGroup: isRtkLinkStoreToMenuGroupEnabled,
      fetchMenuGroupDetail: isRtkFetchMenuGroupDetailEnabled,
    },
    isLoading: isRtkFlagLoading,
  } = useRtkQueryMigrationEndpointsFlag()

  const [linkStoreToMenuGroupRtk] = useLinkStoreToMenuGroupMutation()
  const [fetchMenuGroupDetailRtk] = useLazyFetchMenuGroupDetailQuery()

  const linkedStoreIDs = menuGroupLinkedStoresIDs

  const linkStoreMaxLimit = getGrabXValueFromLocalStorage<number>(
    MerchantPortalFeatureFlag.linkStoreMaxLimit,
  )

  const textMap = useTranslationsMap({
    title: "menu.menuGroup.linkedStore.drawer.storeList.drawerTitle",
    menuGroupNameLabel: "menu.menuGroup.drawer.labels.name",
    placeHolderSearchStore: "menu.stores.placeholder.searchByStoreName",
    confirm: "menu.menuGroup.linkedStore.drawer.storeList.confirmButton",
    maxStoreSelectedMessage: "menu.menuGroup.message.maxStoresSelected",
    success: "common.success",
    linkErrorMessageMFC: "menu.menuGroup.message.linkMFC",
    xStoresSelected: "menu.menuGroup.create.xStoreSelected",
  })

  useEffect(() => {
    if (visible) {
      TroyWebTracker.sendEvent({
        category: EventCategory.menuGroupAddStores,
        action: EventAction.pageView,
        labels: {
          MERCHANT_GROUP_ID: merchantGroupID,
          MENU_GROUP_ID: menuGroupID,
        },
      })
    }
  }, [visible, menuGroupID])

  const [selectedStoreIDs, setSelectedStoreIDs] = useState<string[]>([])

  const handleChangeSelectedRowKeys = (newSelectedStoreIDs: string[]) => {
    // filter already linked store IDs
    const filteredNewSelectedStoreIDs = newSelectedStoreIDs.filter(
      newSelectedStoreID => !linkedStoreIDs.includes(newSelectedStoreID),
    )

    if (newSelectedStoreIDs.length > linkStoreMaxLimit) {
      if ([...selectedStoreIDs, ...linkedStoreIDs].length < linkStoreMaxLimit) {
        setSelectedStoreIDs(
          filteredNewSelectedStoreIDs.slice(
            0,
            linkStoreMaxLimit - linkedStoreIDs.length,
          ),
        )
        return
      }

      notification.error({
        message: textMap.maxStoreSelectedMessage.replace(
          "<x>",
          `${linkStoreMaxLimit}`,
        ),
      })
    }

    setSelectedStoreIDs(filteredNewSelectedStoreIDs)
  }

  const getIsDisabledRow = (outlet: OutletStore) => {
    if (!outlet.menuGroupV2ID) return true

    return linkedStoreIDs.includes(outlet.merchantID)
  }

  const handleGetCheckboxProps = (outlet: OutletStore) => ({
    disabled: getIsDisabledRow(outlet),
  })

  const getRowClassName = (record: OutletStore) => {
    if (getIsDisabledRow(record)) return "row-disabled"
    return ""
  }

  const handleRowClick = (store: OutletStore) => {
    if (getIsDisabledRow(store)) {
      return
    }
    const newSelectedStoreIDs = [...selectedStoreIDs]
    if (newSelectedStoreIDs.indexOf(store.merchantID) >= 0) {
      newSelectedStoreIDs.splice(
        newSelectedStoreIDs.indexOf(store.merchantID),
        1,
      )
    } else {
      newSelectedStoreIDs.push(store.merchantID)
    }
    setSelectedStoreIDs(newSelectedStoreIDs)
  }

  const linkStoreErrorCallback = useCallback(
    (errorResponse: ResponseWithError) => {
      const { message = "" } = errorResponse ?? {}
      if (message.includes("MFC")) {
        return textMap.linkErrorMessageMFC
      }
      return (message.includes("msg=") && message.split("msg=").pop()) || true
    },
    [],
  )

  const linkStoreErrorNotificationContent: GetErrorNotificationContent = ({
    data,
    defaultMessage,
    trace,
  }) => {
    const { message = "" } = (data as ResponseWithError) ?? {}
    let errorMessage = defaultMessage

    if (message.includes("MFC")) {
      errorMessage = textMap.linkErrorMessageMFC
    } else if (message.includes("msg=")) {
      errorMessage = message.split("msg=").pop() || defaultMessage
    }

    return {
      message: errorMessage,
      description: trace ? `trace=${trace}` : undefined,
    }
  }

  const refreshAfterLink = (updatedMenuGroupID: string) =>
    refreshMenuGroupAfterStoreChange({
      updatedMenuGroupID,
      menuGroupID,
      merchantGroupID,
      dispatch,
      isRtkFetchMenuGroupDetailEnabled,
      fetchMenuGroupDetailRtk,
      history,
      onSameMenuGroup: () => fetchAllMenuGroupLinkedStoreList(),
    })

  const handleConfirmClick = useCallback(async () => {
    if (selectedStoreIDs.length) {
      // While migration flags are loading, do not fall back to old Redux: the enabled bit is
      // unknown until GrabX resolves. Legacy-then-RTK for the same user intent can double-call
      // APIs/middleware and splits RTK migration tracking. Wait for !isRtkFlagLoading; disable
      // the trigger UI while loading so clicks are not silently dropped.
      if (isRtkFlagLoading) {
        return
      }

      const t0 = performance.now()
      dispatch(actionCreator.global.showLoading())

      let labels = {
        MERCHANT_GROUP_ID: merchantGroupID,
        MENU_GROUP_ID: menuGroupID,
        NUM_STORES_SELECTED: selectedStoreIDs.length,
        LOADING_STATUS: "SUCCESS",
        LOAD_TIME: 0,
        REASON: "",
        ERROR_MSG: "",
      }

      if (isRtkLinkStoreToMenuGroupEnabled) {
        // RTK Query path
        const result = await linkStoreToMenuGroupRtk({
          merchantGroupID,
          menuGroupID,
          storeIDs: selectedStoreIDs,
          getErrorNotificationContent: linkStoreErrorNotificationContent,
        })

        if (result.error) {
          labels = {
            ...labels,
            LOADING_STATUS: "FAILURE",
            ...getResponseErrorEventLabelsForRTK(result),
          }
        } else {
          const updatedMenuGroupID = result.data?.menuGroupID || menuGroupID
          if (await refreshAfterLink(updatedMenuGroupID)) {
            dispatch(
              actionCreator.menu.toggleDrawer({
                showMenuGroupLinkStore: false,
              }),
            )
            dispatch(actionCreator.global.hideLoading())
            notification.success({
              message: textMap.success,
            })
          } else labels = { ...labels, LOADING_STATUS: "FAILURE" }
        }

        sendLinkStoreConfirmEvents({
          t0,
          labels,
          merchantGroupID,
          isRtkEnabled: isRtkLinkStoreToMenuGroupEnabled,
          fromRtkQuery: true,
        })
      } else {
        // Old Redux path
        const resp: any = await dispatch(
          actionCreator.menu.linkStoreToMenuGroup({
            merchantGroupID,
            menuGroupID,
            storeIDs: selectedStoreIDs,
            errorCallback: linkStoreErrorCallback,
          }),
        )

        if (resp?.type?.endsWith("_SUCCESS")) {
          const updatedMenuGroupID =
            resp?.payload?.data?.menuGroupID || menuGroupID
          if (await refreshAfterLink(updatedMenuGroupID)) {
            dispatch(
              actionCreator.menu.toggleDrawer({
                showMenuGroupLinkStore: false,
              }),
            )
            dispatch(actionCreator.global.hideLoading())
            notification.success({
              message: textMap.success,
            })
          } else labels = { ...labels, LOADING_STATUS: "FAILURE" }
        } else {
          labels = {
            ...labels,
            LOADING_STATUS: "FAILURE",
            ...getResponseErrorEventLabels(resp),
          }
        }

        sendLinkStoreConfirmEvents({
          t0,
          labels,
          merchantGroupID,
          isRtkEnabled: isRtkLinkStoreToMenuGroupEnabled,
          fromRtkQuery: false,
        })
      }

      dispatch(actionCreator.global.hideLoading())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedStoreIDs,
    merchantGroupID,
    menuGroupID,
    isRtkFlagLoading,
    isRtkLinkStoreToMenuGroupEnabled,
    isRtkFetchMenuGroupDetailEnabled,
    fetchMenuGroupDetailRtk,
  ])

  useEffect(() => {
    if (!visible) {
      setSelectedStoreIDs([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible])

  return (
    <Fragment>
      <Drawer
        title={
          <Row type="flex" align="middle" gutter={20}>
            <Col
              css={css`
                cursor: pointer;
                display: flex;
                align-items: center;
                padding-right: 0 !important;
              `}
            >
              <ChevronLeftUniversal
                onClick={() => {
                  dispatch(
                    actionCreator.menu.toggleDrawer({
                      showMenuGroupLinkStore: false,
                    }),
                  )
                }}
              />
            </Col>
            <Col css={drawerTitleStyle}>{textMap.title}</Col>
          </Row>
        }
        placement="right"
        width={850}
        visible={visible}
        onClose={onClose}
        destroyOnClose
        css={menuGroupLinkStoreDrawerStyle}
      >
        <MenuStoreList
          data-testid="menu-store-list"
          title={textMap.xStoresSelected.replace(
            "<x>",
            `${
              [...selectedStoreIDs, ...linkedStoreIDs].length
            }/${linkStoreMaxLimit}`,
          )}
          rowSelection={{
            type: "checkbox",
            selectedRowKeys: [...selectedStoreIDs, ...linkedStoreIDs],
            onChange: handleChangeSelectedRowKeys,
            getCheckboxProps: handleGetCheckboxProps,
            preserveSelectedRowKeys: true,
          }}
          onRowClickOverride={handleRowClick}
          eventCategory={EventCategory.menuGroupAddStores}
          eventLabels={{
            MERCHANT_GROUP_ID: merchantGroupID,
            MENU_GROUP_ID: menuGroupID,
          }}
          rowClassName={getRowClassName}
          disableMenuV1
        />
        <div
          css={css`
            ${drawerFooter}
            ${menuGroupLinkStoreDrawerFooter}
          `}
        >
          <Button
            css={menuGroupLinkStoreDrawerButton}
            disabled={
              !selectedStoreIDs.length ||
              isRtkFlagLoading ||
              [...selectedStoreIDs, ...linkedStoreIDs].length >
                linkStoreMaxLimit
            }
            data-testid="menu-group-drawer-save-button"
            type="primary"
            onClick={handleConfirmClick}
          >
            {textMap.confirm}
          </Button>
        </div>
      </Drawer>
    </Fragment>
  )
}
export const MenuGroupLinkStoreDrawer = Form.create<
  MenuGroupLinkStoreDrawerProps
>({})(MenuGroupLinkStoreDrawerComponent)
